(() => {
  if (customElements.get('mc-upsell-layout')) return;

  const addedProducts = new Map();

  // The theme refreshes line items and totals, but leaves recommendations intact.
  document.addEventListener('cart:change', event => {
    const sections = event.detail.cart?.sections || {};
    const cartPage = document.querySelector('[data-mc-cart-page]');
    const cartPageHTML = sections[cartPage?.dataset.sectionId];
    if (typeof cartPageHTML === 'string') {
      const updatedPage = new DOMParser().parseFromString(cartPageHTML, 'text/html').querySelector('[data-mc-cart-page]');
      if (updatedPage) cartPage.replaceWith(updatedPage);
    }
    if (!event.detail.cart?.item_count) return;
    const html = Object.values(sections).find(value => typeof value === 'string' && value.includes('<cart-drawer'));
    if (!html) return;
    const updated = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer [data-mc-cart-recommendations]');
    if (!updated) return;
    requestAnimationFrame(() => {
      document.querySelector('cart-drawer [data-mc-cart-recommendations]')?.replaceWith(updated);
    });
  });


  class MCUpsellLayout extends HTMLElement {
    connectedCallback() {
      this.addedProducts = addedProducts;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.carousel = this.querySelector('[data-mc-carousel]');
      this.drawer = this.querySelector('x-drawer');
      this.content = this.drawer.querySelector('[data-mc-drawer-content]');
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-mc-product-url]');
        if (button) {
          // The native drawer also delegates aria-controls clicks on document.
          event.preventDefault();
          event.stopPropagation();
          this.openProduct(button);
        }
        if (event.target.closest('[data-mc-prev]')) this.scrollCards(-1);
        if (event.target.closest('[data-mc-next]')) this.scrollCards(1);
      }, { signal });
      this.carousel.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.carousel);
      this.drawer.addEventListener('dialog:after-hide', () => {
        if (this.drawer.open) return;
        this.request?.abort();
        this.content.replaceChildren();
      }, { signal });
      this.status = this.querySelector('[data-mc-added-status]') || this.element('span', 'sr-only');
      this.status.dataset.mcAddedStatus = '';
      this.status.setAttribute('role', 'status');
      this.append(this.status);
      this.renderCards();
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      this.resizeObserver?.disconnect();
      if (this.drawer?.open) {
        Promise.resolve(this.drawer.hide()).then(() => this.drawer.remove());
      }
    }

    renderCards() {
      const cards = this.recommendationCards;
      this.carousel.replaceChildren(...(cards ? [cards.cloneNode(true)] : []));
      // API HTML is shared between placements; target this instance's drawer.
      this.carousel.querySelectorAll('[data-mc-product-url]').forEach(button => {
        button.setAttribute('aria-controls', this.dataset.drawerId);
      });
      this.querySelector('[data-mc-upsell-display]').hidden = !this.carousel.children.length;
      this.updateAddedCards();
      this.carousel.scrollLeft = 0;
      requestAnimationFrame(() => this.updateArrows());
    }

    async loadRecommendations() {
      if (this.recommendationsLoading || !this.dataset.recommendationsUrl) return;
      this.recommendationsLoading = true;
      const { signal } = this.controller;
      try {
        const response = await fetch(this.dataset.recommendationsUrl, { signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted) return;
        const cards = html.querySelector('template[data-mc-api-cards]')?.content;
        if (cards?.children.length) {
          const limit = Math.max(1, Math.min(10, Number(this.dataset.limit) || 6));
          [...cards.children].slice(limit).forEach(card => card.remove());
          this.recommendationCards = cards;
          html.querySelectorAll('[data-mc-product-meta]').forEach(metadata => {
            if (!this.querySelector(`[data-mc-product-meta="${metadata.dataset.mcProductMeta}"]`)) this.append(metadata);
          });
        }
      } catch (error) {
        // Keep the module hidden when the API is empty or unavailable.
        if (signal.aborted) return;
      } finally {
        this.recommendationsLoading = false;
        if (!signal.aborted && this.isConnected) {
          this.renderCards();
        }
      }
    }

    updateAddedCards() {
      this.carousel.querySelectorAll('[data-mc-product-id]').forEach(card => {
        const added = this.addedProducts.get(card.dataset.mcProductId);
        card.classList.toggle('is-added', Boolean(added));
        if (!added) return;
        const trigger = card.querySelector('[data-mc-product-url]');
        const url = new URL(trigger.dataset.mcProductUrl, location.origin);
        url.searchParams.set('variant', added.variant.id);
        trigger.dataset.mcProductUrl = url.pathname + url.search;
        const image = card.querySelector('img.mc-upsell__image');
        if (image && added.image) {
          const imageUrl = new URL(added.image, location.origin);
          imageUrl.searchParams.set('width', '640');
          image.removeAttribute('srcset');
          image.src = imageUrl.href;
          image.alt = `${added.title} — ${added.variant.title}`;
        }
        const prices = card.querySelector('.mc-upsell__prices');
        prices.replaceChildren();
        if (added.variant.compare_at_price > added.variant.price) {
          prices.append(this.element('s', 'mc-upsell__compare', this.money(added.variant.compare_at_price)));
        }
        prices.append(this.element('span', '', this.money(added.variant.price)));
        if (added.variant.compare_at_price > added.variant.price) {
          prices.append(this.element('span', 'mc-upsell__saving', `−${this.money(added.variant.compare_at_price - added.variant.price)}`));
        }
      });
    }

    async markAdded(product, variant) {
      const colourIndex = (this.preorders.swatches || [])[0]?.option;
      const colourImage = colourIndex !== undefined && product.variants.find(candidate => candidate.options[colourIndex] === variant.options[colourIndex] && candidate.featured_image?.src)?.featured_image.src;
      this.addedProducts.set(String(product.id), { variant, title: product.title, image: variant.featured_image?.src || colourImage || product.featured_image });
      document.querySelectorAll('mc-upsell-layout').forEach(layout => layout.updateAddedCards());
      this.status.textContent = `${product.title}: ${this.drawer.dataset.added || 'Added to cart'}`;
      await this.drawer.hide();
    }

    scrollCards(direction) {
      const card = this.carousel.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.carousel).columnGap) || 0;
      const rtl = getComputedStyle(this.carousel).direction === 'rtl' ? -1 : 1;
      this.carousel.scrollBy({ left: direction * rtl * (card.offsetWidth + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    updateArrows() {
      const position = Math.abs(this.carousel.scrollLeft);
      this.querySelector('[data-mc-prev]').disabled = position <= 2;
      this.querySelector('[data-mc-next]').disabled = position >= this.carousel.scrollWidth - this.carousel.clientWidth - 2;
    }

    async openProduct(button) {
      await customElements.whenDefined('x-drawer');
      if (!this.isConnected) return;
      const cartDrawer = this.cartDrawer = this.closest('cart-drawer');
      if (cartDrawer?.open) await cartDrawer.hide();
      if (!this.isConnected) return;
      this.drawer.querySelector('[data-mc-drawer-title]').textContent = button.dataset.productTitle;
      this.drawer.querySelector('[data-mc-product-link]').href = button.dataset.mcProductUrl;
      this.drawer.show();
      this.loadProduct(button.dataset.mcProductUrl);
    }

    async loadProduct(productUrl) {
      this.request?.abort();
      const request = this.request = new AbortController();
      this.content.setAttribute('aria-busy', 'true');
      const status = document.createElement('p');
      status.className = 'mc-upsell-drawer__status';
      status.setAttribute('role', 'status');
      status.textContent = this.drawer.dataset.loading;
      this.content.replaceChildren(status);
      try {
        const url = new URL(productUrl, location.origin);
        const requestedVariant = url.searchParams.get('variant');
        url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
        url.search = '';
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        if (!Array.isArray(product.variants) || !product.variants.length) throw new Error('Product missing variants');
        if (request.signal.aborted || !this.drawer.open) return;
        const metadata = this.querySelector(`[data-mc-product-meta="${product.id}"]`);
        this.preorders = metadata ? JSON.parse(metadata.textContent) : { variants: {} };
        this.renderProduct(product, productUrl, requestedVariant);
      } catch (error) {
        if (request.signal.aborted) return;
        status.textContent = this.drawer.dataset.error;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button';
        retry.textContent = this.drawer.dataset.retry;
        retry.addEventListener('click', () => this.loadProduct(productUrl), { once: true });
        this.content.replaceChildren(status, retry);
      } finally {
        if (this.request === request) this.content.removeAttribute('aria-busy');
      }
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    money(amount) {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || window.Shopify.currency.active
      }).format(amount / 100);
    }

    renderProduct(product, productUrl, requestedVariant) {
      const root = this.element('div', 'mc-upsell-product');
      const selected = product.variants.find(v => String(v.id) === requestedVariant) || product.variants.find(v => v.available) || product.variants[0];
      const gallery = this.element('div', 'mc-upsell-product__gallery');
      const slides = this.element('div', 'mc-upsell-product__slides');
      slides.id = `${this.dataset.drawerId}-images`;
      const images = product.images || [];
      images.forEach((source, index) => {
        const img = this.element('img', 'mc-upsell-product__image');
        const imageUrl = new URL(typeof source === 'string' ? source : source.src, location.origin);
        imageUrl.searchParams.set('width', '900');
        img.src = imageUrl.href;
        img.alt = product.title;
        img.loading = index ? 'lazy' : 'eager';
        slides.append(img);
      });
      gallery.append(slides);
      if (images.length > 1) {
        const controls = this.element('div', 'mc-upsell-product__gallery-controls');
        const previous = this.element('button', '', '‹');
        const next = this.element('button', '', '›');
        const count = this.element('span');
        [previous, next].forEach(button => { button.type = 'button'; button.setAttribute('aria-controls', slides.id); });
        previous.setAttribute('aria-label', this.drawer.dataset.previous);
        next.setAttribute('aria-label', this.drawer.dataset.next);
        const scroll = direction => slides.scrollBy({ left: direction * slides.clientWidth * (getComputedStyle(slides).direction === 'rtl' ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        previous.addEventListener('click', () => scroll(-1));
        next.addEventListener('click', () => scroll(1));
        const update = () => {
          const index = Math.round(Math.abs(slides.scrollLeft) / (slides.clientWidth || 1));
          count.textContent = `${index + 1} / ${images.length}`;
          previous.disabled = index === 0;
          next.disabled = index >= images.length - 1;
        };
        slides.addEventListener('scroll', update, { passive: true });
        controls.append(previous, count, next);
        gallery.append(controls);
        requestAnimationFrame(update);
      }
      if (images.length) root.append(gallery);
      const info = this.element('div', 'mc-upsell-product__info');
      info.append(this.element('h2', 'mc-upsell-product__title', product.title));
      const prices = this.element('div', 'mc-upsell-product__prices');
      prices.setAttribute('aria-live', 'polite');
      info.append(prices);
      const form = this.element('form', 'mc-upsell-product__form');
      const options = [...selected.options];
      const groups = [];
      product.options.forEach((option, index) => {
        if (product.variants.length === 1 && product.variants[0].title === 'Default Title') return;
        const fieldset = this.element('fieldset', 'mc-upsell-product__option');
        const name = typeof option === 'string' ? option : option.name;
        const isColour = (this.preorders.swatches || []).some(swatch => swatch.option === index) || /colou?r/i.test(name);
        if (isColour) fieldset.classList.add('mc-upsell-product__option--colour');
        const legend = this.element('legend', '', name);
        const buttons = this.element('div', 'mc-upsell-product__values');
        [...new Set(product.variants.map(v => v.options[index]))].forEach(value => {
          const button = this.element('button', 'mc-upsell-product__value', value);
          button.type = 'button';
          button.dataset.value = value;
          const swatch = (this.preorders.swatches || []).find(swatch => swatch.option === index && swatch.value === value);
          const colour = swatch?.color || (isColour && CSS.supports('color', value) ? value : '');
          if (isColour && (colour || swatch?.image)) {
            button.classList.add('mc-upsell-product__swatch');
            button.replaceChildren(this.element('span', 'sr-only', value));
            button.title = value;
            if (colour) button.style.setProperty('--mc-swatch-color', colour);
            if (swatch?.image) {
              const image = this.element('img');
              image.src = swatch.image;
              image.alt = '';
              button.append(image);
            }
          }
          button.addEventListener('click', () => { options[index] = value; updateVariant(); });
          buttons.append(button);
        });
        fieldset.append(legend, buttons);
        groups.push({ fieldset, buttons, index, legend, name, isColour });
        form.append(fieldset);
      });
      const add = this.element('button', 'mc-upsell-product__add');
      add.type = 'submit';
      const error = this.element('p', 'mc-upsell-product__error');
      error.setAttribute('role', 'alert');
      error.hidden = true;
      form.append(add, error);
      let variant;
      let submitting = false;
      const updateVariant = () => {
        variant = product.variants.find(v => v.options.every((value, index) => options[index] === value));
        groups.forEach(({ legend, name, isColour, index }) => { if (isColour) legend.textContent = `${name}: ${options[index]}`; });
        groups.forEach(({ buttons, index }) => [...buttons.children].forEach(button => {
          button.setAttribute('aria-pressed', String(button.dataset.value === options[index]));
          // Earlier selections constrain later options; colors remain switchable.
          const available = product.variants.some(v => v.available && v.options[index] === button.dataset.value && v.options.slice(0, index).every((value, i) => options[i] === value));
          button.classList.toggle('is-unavailable', !available);
        }));
        prices.replaceChildren();
        if (variant) {
          const sale = variant.compare_at_price > variant.price;
          prices.append(this.element('span', sale ? 'mc-upsell-product__sale' : '', this.money(variant.price)));
          if (sale) {
            prices.append(this.element('s', 'mc-upsell-product__compare', this.money(variant.compare_at_price)));
            prices.append(this.element('span', 'mc-upsell-product__saving', `−${this.money(variant.compare_at_price - variant.price)}`));
          }
          const link = new URL(productUrl, location.origin);
          link.searchParams.set('variant', variant.id);
          this.drawer.querySelector('[data-mc-product-link]').href = link.pathname + link.search;
          const imageIndex = images.findIndex(source => {
            const src = typeof source === 'string' ? source : source.src;
            return variant.featured_image?.src && new URL(src, location.origin).pathname === new URL(variant.featured_image.src, location.origin).pathname;
          });
          if (imageIndex >= 0) requestAnimationFrame(() => {
            slides.scrollLeft = imageIndex * slides.clientWidth * (getComputedStyle(slides).direction === 'rtl' ? -1 : 1);
          });
        }
        add.disabled = submitting || !variant?.available;
        add.textContent = !variant ? this.drawer.dataset.unavailable : !variant.available ? this.drawer.dataset.soldOut : this.preorders.variants[String(variant.id)] ? this.drawer.dataset.preorder : this.drawer.dataset.add;
        error.hidden = true;
      };
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (submitting || !variant?.available) return;
        submitting = true;
        add.disabled = true;
        add.setAttribute('aria-busy', 'true');
        groups.forEach(({ fieldset }) => { fieldset.disabled = true; });
        const selectedVariant = variant;
        const cartDrawer = this.cartDrawer;
        let added = false;
        try {
          const sections = ['variant-added'];
          const cartPageSection = document.querySelector('[data-mc-cart-page]')?.dataset.sectionId;
          if (cartPageSection) sections.push(cartPageSection);
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
          const data = { items: [{ id: selectedVariant.id, quantity: 1 }], sections, sections_url: cartPageSection ? location.pathname : `${Shopify.routes.root}variants/${selectedVariant.id}` };
          if (this.preorders.variants[String(selectedVariant.id)]) data.items[0].properties = { 'Pre-order': `Delivery est. ${this.preorders.delivery || ''}` };
          const response = await fetch(`${Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(data) });
          const result = await response.json();
          if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.drawer.dataset.error);
          added = true;
          window.maisonTrackUpsellAdd?.(this.closest('[data-mc-cart-recommendations]') ? 'cart' : 'pdp');
          await this.markAdded(product, selectedVariant);
          const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
          if (!cartResponse.ok) throw new Error('Cart unavailable');
          const cart = await cartResponse.json();
          cart.sections = result.sections;
          (root.isConnected ? root : document.documentElement).dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items || [result], cart, blockCartDrawerOpening: true } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
          if (cartDrawer?.isConnected) {
            await new Promise(requestAnimationFrame);
            cartDrawer.show();
          }
        } catch (failure) {
          if (added) {
            // Addition succeeded. Keep its confirmation even if the secondary cart refresh fails.
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            if (cartDrawer?.isConnected) cartDrawer.show();
            return;
          }
          error.textContent = failure.message || this.drawer.dataset.error;
          error.hidden = false;
        } finally {
          submitting = false;
          add.disabled = !variant?.available;
          add.removeAttribute('aria-busy');
          groups.forEach(({ fieldset }) => { fieldset.disabled = false; });
        }
      });
      info.append(form);
      if (product.description) {
        const description = this.element('div', 'mc-upsell-product__description');
        const toggle = this.element('button', 'mc-upsell-product__description-toggle', this.drawer.dataset.description);
        toggle.type = 'button';
        toggle.setAttribute('aria-expanded', 'false');
        const panel = this.element('div', 'mc-upsell-product__description-panel');
        panel.id = `${this.dataset.drawerId}-description`;
        panel.inert = true;
        toggle.setAttribute('aria-controls', panel.id);
        toggle.addEventListener('click', () => {
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          panel.inert = !open;
          description.classList.toggle('is-open', open);
        });
        const clip = this.element('div', 'mc-upsell-product__description-clip');
        description.append(toggle, panel);
        const body = this.element('div', 'mc-upsell-product__description-body');
        const parsed = new DOMParser().parseFromString(product.description, 'text/html');
        parsed.querySelectorAll('script, style, iframe, object, embed, link, form').forEach(node => node.remove());
        parsed.querySelectorAll('*').forEach(node => [...node.attributes].forEach(attribute => {
          if (/^on/i.test(attribute.name) || ['style', 'class', 'id'].includes(attribute.name) || /^\s*javascript:/i.test(attribute.value)) node.removeAttribute(attribute.name);
        }));
        body.append(...parsed.body.childNodes);
        clip.append(body);
        panel.append(clip);
        info.append(description);
      }
      root.append(info);
      this.content.replaceChildren(root);
      updateVariant();
    }
  }

  customElements.define('mc-upsell-layout', MCUpsellLayout);
})();
